import {
  getInitialFilterData,
  getLabelsToValues,
  updateFilterFromParams,
  reduceItemsByFilter,
  getFilterUsingKeys,
  updateFilterFromAction,
  updateFilterToUrl,
  syncFilterAction,
  syncFilterData,
  produceAPIParams
} from 'utils/filter'

import { updateUrlWithParams, getLatestUrlParams } from 'utils/url'
// store for filter select key order
class FilterStore {
  constructor({ fundItems, rule, terms }) {
    // this.optionsMap looks like: {all: ['a', 'b'], valid: ['a']}
    this.optionsMap = {}
    this.keys = []
    this.fundItems = fundItems
    this.rule = rule
    this.terms = terms
    this.initialFilterData = null
    this.labelsToValues = null
    this.currentState = null
    this.init()
  }

  init() {
    const { fundItems, rule, terms } = this
    const initialFilterData = getInitialFilterData(fundItems, rule, terms)
    const { blocks, labelValuePairs } = initialFilterData
    blocks.forEach((block) => {
      block.elements.forEach((elem) => {
        if (elem.type === 'check' || elem.type === 'select') {
          const all = elem.options.map((option) => option.label)
          this.optionsMap[elem.key] = {
            all,
            valid: all
          }
        }
      })
    })
    this.initialFilterData = JSON.parse(JSON.stringify(initialFilterData))
    Object.freeze(this.initialFilterData)
    this.currentState = { filterData: initialFilterData }
    if (labelValuePairs) {
      this.labelsToValues = getLabelsToValues(fundItems, labelValuePairs)
    }
    return this
  }

  purifyValidOptions(currentKey, filteredData) {
    if (this.keys.length === 0) {
      return this
    }
    const { blocks } = filteredData
    let currentPos = this.keys.indexOf(currentKey)
    // -1 means empty some one filter selection
    if (currentPos === -1) {
      currentPos = 0
      if (this.optionsMap[this.keys[0]]) {
        this.optionsMap[this.keys[0]].valid = this.optionsMap[this.keys[0]].all
      }
    }
    const ignoreKeys = this.keys.slice(0, currentPos + 1)
    blocks.forEach((block) => {
      block.elements.forEach((elem) => {
        if (!ignoreKeys.includes(elem.key)) {
          if (elem.type === 'check' || elem.type === 'select') {
            const options = elem.options.map((option) => option.label)
            this.optionsMap[elem.key].valid = options
          }
        }
      })
    })
    return this
  }

  getValidOptions(key) {
    return this.optionsMap[key].valid
  }

  getLabelsToValues() {
    return this.labelsToValues
  }

  add(key) {
    if (!this.keys.includes(key)) {
      this.keys.push(key)
    }
    return this
  }

  purifyOrder(usingKeys) {
    this.keys = this.keys.filter((key) => usingKeys.includes(key))
    return this
  }

  updateKeyOrderToUrl() {
    updateUrlWithParams({ query: { keyOrder: this.keys } })
    return this
  }

  updateKeyOrderFromUrl(url) {
    this.keys = []
    const { keyOrder } = getLatestUrlParams({ href: url, keys: ['keyOrder'] })
    if (keyOrder) {
      this.keys = Array.isArray(keyOrder) ? keyOrder : [keyOrder]
    }
    return this
  }

  clear() {
    this.keys = []
    const keys = Object.keys(this.optionsMap)
    keys.forEach((key) => {
      this.optionsMap[key].valid = this.optionsMap[key].all
    })
    return this
  }

  getOrder() {
    return this.keys
  }

  siftFilter(data) {
    const { blocks } = data
    return {
      ...data,
      blocks: blocks.map((block) => {
        const { elements } = block
        return {
          ...block,
          elements: elements.map((elem) => {
            if (elem.type === 'check') {
              const { options } = elem
              const validOptions = this.getValidOptions(elem.key)
              return {
                ...elem,
                options: options.map((option) => {
                  const disabled = !validOptions.includes(option.label)
                  const { checked } = option
                  return {
                    ...option,
                    disabled,
                    checked: disabled ? false : checked
                  }
                })
              }
            }
            if (elem.type === 'select') {
              const { options } = elem
              const validOptions = this.getValidOptions(elem.key)
              const value = elem.value.filter((option) => validOptions.includes(option))
              return {
                ...elem,
                options: options.map((option) => {
                  const disabled = !validOptions.includes(option.label)
                  const { checked } = option
                  return {
                    ...option,
                    disabled,
                    checked: disabled ? false : checked
                  }
                }),
                value
              }
            }
            return elem
          })
        }
      })
    }
  }

  calcFilterData() {
    const { fundItems, rule, terms } = this
    const initialFilterData = JSON.parse(JSON.stringify(this.initialFilterData))
    let ret = initialFilterData
    const paramObj = getLatestUrlParams({ href: window.location.href })
    if (this.keys.length > 0) {
      ret = this.keys.reduce((acc, key, idx, src) => {
        const tempOrder = src.slice(0, idx + 1)
        const allData = updateFilterFromParams(acc, paramObj, tempOrder)
        const filteredItems = reduceItemsByFilter(fundItems, allData)
        const reducedData = getInitialFilterData(filteredItems, rule, terms)
        this.purifyValidOptions(key, reducedData)
        return this.siftFilter(allData)
      }, initialFilterData)
    }
    this.currentState = { filterData: ret }
    return ret
  }

  transferState(_action) {
    const state = this.currentState
    const action = syncFilterAction(_action, state.filterData)
    const filterData = updateFilterFromAction(state.filterData, action)
    updateFilterToUrl(filterData)
    const usingKeys = getFilterUsingKeys(filterData)
    if (action.clearAll || usingKeys.length === 0) {
      this.clear()
    } else {
      this.add(action.id).purifyOrder(usingKeys).purifyValidOptions(action.id, filterData)
    }
    const siftedData = this.calcFilterData()
    // ====== this section code is for purifying filer data again =====
    // e.g. current selections is 'item1','item2','itemA' but when you delete only 'item1' with 'itemA' removed as well, so we have to recalculate the filter data according to the latest fundItems
    updateFilterToUrl(siftedData)
    const purifiedData = this.calcFilterData()
    updateFilterToUrl(purifiedData)
    // ===========================
    this.updateKeyOrderToUrl()
    const finalPurifiedData = syncFilterData(purifiedData)
    this.currentState = {
      ...state,
      filterData: finalPurifiedData,
      currentFilterKey: action.id
    }
    return JSON.parse(JSON.stringify(this.currentState))
  }

  getFundItems() {
    return this.fundItems
  }

  produceAPIParams() {
    return produceAPIParams(this.labelsToValues, this.currentState.filterData)
  }
}

export default FilterStore
